import { useState, useMemo, useCallback } from 'react';

interface FlowStep {
  id: string;
  label: string;
}

interface UseMultiStepFlowReturn {
  /** The ID of the currently active internal step. */
  internalStep: string;
  /** Directly set the active step (use for reset / error-path jumps). */
  setInternalStep: (_step: string) => void;
  /** Set of step IDs that have been passed through via goToNext. */
  completedSteps: Set<string>;
  /**
   * Index of the current step within the supplied steps array.
   * Returns steps.length when on the result screen so all steps render as completed.
   * Falls back to 0 if the current step ID is not found in the array.
   */
  currentStepIndex: number;
  /** True when internalStep === 'result'. */
  isResultScreen: boolean;
  /** Steps array enriched with completed / current flags, ready for the stepper. */
  stepsWithCompletion: Array<
    FlowStep & { completed: boolean; current: boolean }
  >;
  /** Mark the current step complete and advance to the next step or 'result'. */
  goToNext: () => void;
  /** Go back to the previous step. No-op when already on the first step. */
  goToPrev: () => void;
}

/**
 * Manages the shared state machine that all portal multi-step workflow layouts
 * share: step tracking, completion set, stepper index, and result-screen flag.
 *
 * Layouts are free to use only the parts they need and keep domain-specific
 * logic (submission, validation gating, custom step mapping) themselves.
 *
 * @param steps    Ordered array of user-interactive steps (does NOT include the result step).
 * @param initialStep  ID of the first step.
 */
export function useMultiStepFlow(
  steps: readonly FlowStep[],
  initialStep: string
): UseMultiStepFlowReturn {
  const [internalStep, setInternalStep] = useState(initialStep);
  const [completedSteps, setCompletedSteps] = useState<Set<string>>(new Set());

  const isResultScreen = internalStep === 'result';

  const currentStepIndex = useMemo(() => {
    if (isResultScreen) return steps.length;
    const idx = steps.findIndex((s) => s.id === internalStep);
    return idx === -1 ? 0 : idx;
  }, [steps, internalStep, isResultScreen]);

  const stepsWithCompletion = useMemo(
    () =>
      steps.map((_step, index) => ({
        ..._step,
        completed: completedSteps.has(_step.id),
        current: index === currentStepIndex,
      })),
    [steps, completedSteps, currentStepIndex]
  );

  const goToNext = useCallback(() => {
    const currentIndex = steps.findIndex((s) => s.id === internalStep);
    setCompletedSteps((prev) => new Set([...prev, internalStep]));
    if (currentIndex + 1 < steps.length) {
      setInternalStep(steps[currentIndex + 1]!.id);
    } else {
      setInternalStep('result');
    }
  }, [steps, internalStep]);

  const goToPrev = useCallback(() => {
    const currentIndex = steps.findIndex((s) => s.id === internalStep);
    if (currentIndex > 0) {
      setInternalStep(steps[currentIndex - 1]!.id);
    }
  }, [steps, internalStep]);

  return {
    internalStep,
    setInternalStep,
    completedSteps,
    currentStepIndex,
    isResultScreen,
    stepsWithCompletion,
    goToNext,
    goToPrev,
  };
}
